import * as React from 'react'
import { Check, Copy, InfoIcon } from 'lucide-react'

import type { RouterOutputs } from '@package/api'
import { Avatar, AvatarFallback, AvatarImage, Button, ScrollArea, Separator } from '@ui/web/components'
import { getInitials } from '@helper/utils/format'
import { useHandleCopy } from '@helper/web/hooks'
import cn from '@config/tailwind/cn'

interface KidsListProps {
  className?: string
  data: RouterOutputs['myOrganization']['listChildren']['data']
}

export function KidsList({ className, data }: KidsListProps) {
  const { isCopied, handleCopy } = useHandleCopy()
  const hasSchoolClass = data.some((item) => item.schoolClass != null)

  return (
    <ScrollArea
      className={cn(
        'srd:h-[200px] srd:w-full srd:rounded-md srd:border srd:border-neutrals-n-35 srd:text-neutrals-black',
        className,
      )}
    >
      <div className='srd:w-full srd:min-w-0'>
        {(() => {
          if (data.length === 0) {
            return (
              <div className='srd:flex srd:flex-col srd:gap-y-2 srd:items-center srd:justify-center srd:h-[200px]'>
                <InfoIcon className='srd:h-4 srd:w-4' />
                <p className='srd:text-xs srd:font-normal'>Tidak ada data</p>
              </div>
            )
          }

          return data.map((item) => (
            <React.Fragment key={item.id}>
              <div
                className={cn(
                  'srd:grid srd:w-full srd:min-w-0 srd:items-center srd:gap-x-2 srd:px-2 srd:py-2',
                  hasSchoolClass
                    ? 'srd:grid-cols-[minmax(0,1fr)_minmax(0,110px)_auto]'
                    : 'srd:grid-cols-[minmax(0,1fr)_auto]',
                )}
              >
                <div className='srd:flex srd:min-w-0 srd:items-center srd:gap-x-2 srd:overflow-hidden'>
                  <Avatar profile='user' className='srd:size-8 srd:shrink-0'>
                    <AvatarImage src={item.image ?? undefined} />
                    <AvatarFallback>{getInitials(item.name)}</AvatarFallback>
                  </Avatar>

                  <p className='srd:min-w-0 srd:truncate srd:text-sm srd:font-medium'>{item.name}</p>
                </div>

                {hasSchoolClass && (
                  <div className='srd:flex srd:min-w-0 srd:items-center srd:gap-x-1 srd:overflow-hidden'>
                    {item.schoolClass ? (
                      <>
                        <Avatar profile='user' className='srd:size-5 srd:shrink-0'>
                          <AvatarImage src={item.schoolClass.image ?? undefined} />
                          <AvatarFallback>{getInitials(item.schoolClass.name)}</AvatarFallback>
                        </Avatar>

                        <p className='srd:min-w-0 srd:truncate srd:text-sm srd:font-normal'>{item.schoolClass.name}</p>
                      </>
                    ) : null}
                  </div>
                )}

                <div className='srd:flex srd:items-center srd:justify-end srd:gap-x-1'>
                  <p className='srd:text-sm srd:font-normal srd:tabular-nums'>{item.pin}</p>

                  <Button
                    variant='link'
                    className='srd:h-fit srd:w-fit srd:p-0'
                    onClick={() => handleCopy(item.pin ?? '', item.id)}
                    disabled={isCopied(item.id)}
                  >
                    {isCopied(item.id) ? <Check className='srd:size-4' /> : <Copy className='srd:size-4' />}
                  </Button>
                </div>
              </div>
              <Separator className='srd:my-0' />
            </React.Fragment>
          ))
        })()}
      </div>
    </ScrollArea>
  )
}
